import {flow} from 'lodash';

import {type DynamoPropsInput} from './types';

import {setIn, updateIn} from '@packages/systems/core/utils/ImmUtils';

import {
  isCommerceOrderWrapperElement,
  isSearchResultWrapper,
  isCommerceShippingMethodItem,
  isCommerceCheckoutOrderSummaryExtraItemsListItem,
  isCommerceCartWrapper,
  isCommerceCartItemElement,
  isCommerceCheckoutOrderItem,
  isCommerceAddToCartOption,
  isCommerceCartOptionListItem,
  isCommerceCheckoutOrderItemOptionListItem,
  isCommerceDownloadsItem,
  isUserAccountSubscriptionListItem,
} from '@packages/utilities/elementTypeUtils';

import {
  COMMERCE_ORDER_ROOT_COLLECTION,
  COMMERCE_ORDER_ROOT_COLLECTION_ID,
  COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION,
  COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION_ID,
  COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION,
  COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION_ID,
  COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID,
  COMMERCE_PRODUCT_OPTIONS_COLLECTION,
  COMMERCE_PRODUCT_OPTIONS_COLLECTION_ID,
  COMMERCE_CART_COLLECTION,
  COMMERCE_CART_COLLECTION_ID,
  COMMERCE_DOWNLOAD_FILES_COLLECTION,
  COMMERCE_DOWNLOAD_FILES_COLLECTION_ID,
  COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION,
  COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION_ID,
  withOrderItemsCollection as _withOrderItemsCollection,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';

import {
  USER_SUBSCRIPTION_COLLECTION,
  USER_SUBSCRIPTION_COLLECTION_ID,
} from '@packages/systems/users/gateway';

import {
  SEARCH_COLLECTION,
  SEARCH_COLLECTION_ID,
} from '@packages/systems/core/constants/SiteSearch';

// Order root collection updaters
const addOrderRootCollection = setIn(
  ['Dynamo', 'collections', COMMERCE_ORDER_ROOT_COLLECTION_ID],
  COMMERCE_ORDER_ROOT_COLLECTION
);
const setOrderRootCollectionId = setIn(
  ['Dynamo', 'collectionId'],
  COMMERCE_ORDER_ROOT_COLLECTION_ID
);
const withOrderRootCollection = flow(
  addOrderRootCollection,
  setOrderRootCollectionId
);

// Order items collection updaters
const addOrderItemsCollection = updateIn(_withOrderItemsCollection, [
  'Dynamo',
  'collections',
]);
const setOrderItemsCollectionId = setIn(
  ['Dynamo', 'collectionId'],
  COMMERCE_ORDER_USER_ITEMS_COLLECTION_ID
);
const withOrderItemsCollection = flow(
  addOrderItemsCollection,
  setOrderItemsCollectionId
);

// Shipping Methods collection updaters
const addShippingMethodsCollection = setIn(
  ['Dynamo', 'collections', COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION_ID],
  COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION
);
const setShippingMethodsCollectionId = setIn(
  ['Dynamo', 'collectionId'],
  COMMERCE_ORDER_SHIPPING_METHODS_COLLECTION_ID
);
const withShippingMethodsCollection = flow(
  addShippingMethodsCollection,
  setShippingMethodsCollectionId
);

// Extra Items collection updaters
const addExtraItemsCollection = setIn(
  ['Dynamo', 'collections', COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION_ID],
  COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION
);
const setExtraItemsCollectionId = setIn(
  ['Dynamo', 'collectionId'],
  COMMERCE_ORDER_EXTRA_ITEMS_COLLECTION_ID
);
const withExtraItemsCollection = flow(
  addExtraItemsCollection,
  setExtraItemsCollectionId
);

// Cart collection updaters
const addCartCollection = setIn(
  ['Dynamo', 'collections', COMMERCE_CART_COLLECTION_ID],
  COMMERCE_CART_COLLECTION
);
const setCartCollectionId = setIn(
  ['Dynamo', 'collectionId'],
  COMMERCE_CART_COLLECTION_ID
);
const withCartCollection = flow(addCartCollection, setCartCollectionId);

const addProductOptionsCollection = setIn(
  ['Dynamo', 'collections', COMMERCE_PRODUCT_OPTIONS_COLLECTION_ID],
  COMMERCE_PRODUCT_OPTIONS_COLLECTION
);
const setProductOptionsCollectionId = setIn(
  ['Dynamo', 'collectionId'],
  COMMERCE_PRODUCT_OPTIONS_COLLECTION_ID
);
const withProductOptionsCollection = flow(
  addProductOptionsCollection,
  setProductOptionsCollectionId
);

const addAtcProductOptionsCollection = setIn(
  ['Dynamo', 'collections', COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION_ID],
  COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION
);
const setAtcProductOptionsCollectionId = setIn(
  ['Dynamo', 'collectionId'],
  COMMERCE_ADD_TO_CART_PRODUCT_OPTIONS_COLLECTION_ID
);
const withAtcProductOptionsCollection = flow(
  addAtcProductOptionsCollection,
  setAtcProductOptionsCollectionId
);

const addDownloadFilesCollection = setIn(
  ['Dynamo', 'collections', COMMERCE_DOWNLOAD_FILES_COLLECTION_ID],
  COMMERCE_DOWNLOAD_FILES_COLLECTION
);
const setDownloadFilesCollectionId = setIn(
  ['Dynamo', 'collectionId'],
  COMMERCE_DOWNLOAD_FILES_COLLECTION_ID
);
const withDownloadFilesCollection = flow(
  addDownloadFilesCollection,
  setDownloadFilesCollectionId
);

// Search collection updaters
const addSearchCollection = setIn(
  ['Dynamo', 'collections', SEARCH_COLLECTION_ID],
  SEARCH_COLLECTION
);
const setSearchCollectionId = setIn(
  ['Dynamo', 'collectionId'],
  SEARCH_COLLECTION_ID
);
const withSearchCollection = flow(addSearchCollection, setSearchCollectionId);

// User subscription collection updaters
const addUserSubscriptionsCollection = setIn(
  ['Dynamo', 'collections', USER_SUBSCRIPTION_COLLECTION_ID],
  USER_SUBSCRIPTION_COLLECTION
);
const setUserSubscriptionsCollectionId = setIn(
  ['Dynamo', 'collectionId'],
  USER_SUBSCRIPTION_COLLECTION_ID
);
const withUserSubscriptionsCollection = flow(
  addUserSubscriptionsCollection,
  setUserSubscriptionsCollectionId
);

export function addNonDynamoCollectionIfNeeded(
  props: DynamoPropsInput
): DynamoPropsInput {
  const {originalElement, pluginsContext} = props;
  if (isCommerceCartWrapper(originalElement)) {
    return {
      ...props,
      pluginsContext: pluginsContext.withMutations(withCartCollection),
    };
  } else if (isCommerceAddToCartOption(originalElement)) {
    return {
      ...props,
      pluginsContext: pluginsContext.withMutations(
        withAtcProductOptionsCollection
      ),
    };
  } else if (
    isCommerceCartOptionListItem(originalElement) ||
    isCommerceCheckoutOrderItemOptionListItem(originalElement)
  ) {
    return {
      ...props,
      pluginsContext: pluginsContext.withMutations(
        withProductOptionsCollection
      ),
    };
  } else if (isCommerceDownloadsItem(originalElement)) {
    return {
      ...props,
      pluginsContext: pluginsContext.withMutations(withDownloadFilesCollection),
    };
  } else if (isCommerceOrderWrapperElement(originalElement)) {
    return {
      ...props,
      pluginsContext: pluginsContext.withMutations(withOrderRootCollection),
    };
  } else if (
    isCommerceCartItemElement(originalElement) ||
    isCommerceCheckoutOrderItem(originalElement)
  ) {
    return {
      ...props,
      pluginsContext: pluginsContext.withMutations(withOrderItemsCollection),
    };
  } else if (isCommerceShippingMethodItem(originalElement)) {
    return {
      ...props,
      pluginsContext: pluginsContext.withMutations(
        withShippingMethodsCollection
      ),
    };
  } else if (
    isCommerceCheckoutOrderSummaryExtraItemsListItem(originalElement)
  ) {
    return {
      ...props,
      pluginsContext: pluginsContext.withMutations(withExtraItemsCollection),
    };
  } else if (isSearchResultWrapper(originalElement)) {
    return {
      ...props,
      pluginsContext: pluginsContext.withMutations(withSearchCollection),
    };
  } else if (isUserAccountSubscriptionListItem(originalElement)) {
    return {
      ...props,
      pluginsContext: pluginsContext.withMutations(
        withUserSubscriptionsCollection
      ),
    };
  } else {
    return props;
  }
}
